/**
 * Market Size (TAM/SAM/SOM) content parser — mirrors backend/pitch_market_size.py.
 *
 * The Market Size slide has a bespoke visualisation (three nested rings +
 * label / big number / description column) that requires structured input.
 * The AI draft prompt emits this shape natively:
 *
 *     TAM — ~250,000–300,000 SMEs
 *     Formal and formalizing Kenyan SMEs with growth ambitions...
 *
 *     SAM — ~15,000–40,000 SMEs
 *     Digitally reachable, record-keeping SMEs actively raising $10K–$2M...
 *
 *     SOM — ~1,500–4,000 businesses
 *     Captured within 3 years (10% of SAM), driven by...
 *
 * Legacy decks may still hold plain single-line bullets ("- TAM: 250K SMEs").
 * `parseMarketSize` gracefully handles both — modern shape yields rich
 * {label,value,description} triplets; legacy shape yields description="" so
 * the layout still renders without a blank right pane.
 */

const RING_HEADER = /^\s*(?:[-*•●▪]\s*)?(TAM|SAM|SOM)\b\s*[—–\-:]?\s*(.*)$/i;

/**
 * @param {string} content
 * @returns {Array<{label:'TAM'|'SAM'|'SOM', value:string, description:string}>}
 */
export function parseMarketSize(content) {
  if (!content || typeof content !== "string") return [];
  const lines = content.replace(/\r\n/g, "\n").split("\n").map((l) => l.trimEnd());

  const rings = {};
  const order = [];
  let current = null;
  let buf = [];

  const flush = () => {
    if (current !== null) {
      const desc = buf
        .map((s) => s.trim())
        .filter(Boolean)
        .join(" ")
        .replace(/\s*[•●▪]\s*/g, " ")
        .trim();
      rings[current].description = desc;
    }
    buf = [];
  };

  for (const ln of lines) {
    const stripped = ln.trim();
    const m = stripped.match(RING_HEADER);
    // Only treat as a ring header if the label appears at the start of the
    // meaningful content (after any leading bullet marker). Rules out
    // accidental mid-sentence matches like "Our TAM number is 100K".
    const contentAfterBullet = stripped.replace(/^\s*[-*•●▪]\s*/, "");
    if (m && contentAfterBullet.toUpperCase().startsWith(m[1].toUpperCase())) {
      flush();
      const label = m[1].toUpperCase();
      const value = (m[2] || "").trim().replace(/^[—–\-:]/, "").trim();
      if (!(label in rings)) {
        rings[label] = { label, value, description: "" };
        order.push(label);
      } else if (value.length > rings[label].value.length) {
        rings[label].value = value;
      }
      current = label;
    } else if (current !== null && stripped) {
      buf.push(stripped);
    }
  }
  flush();

  return ["TAM", "SAM", "SOM"].filter((k) => k in rings).map((k) => rings[k]);
}

/**
 * True when the content follows the modern shape well enough to render the
 * rich nested-funnel layout with real data. Renderers can fall back to a
 * placeholder-friendly compact form otherwise.
 */
export function hasRichMarketSize(content) {
  const rings = parseMarketSize(content);
  if (rings.length < 2) return false;
  return rings.some((r) => r.value && r.description);
}
